/**
 * Dev fixture to reproduce Kiran #8070 — flat Supporting Documents shows
 * attachments but Grouped view + Summary appendix are empty (itssecure).
 *
 * Root cause under investigation: legacy snake_case `request_section` values
 * appear in flat list but `getStageFromSection` returns null for grouped/Summary.
 *
 * Web (yarn web): open an existing local request, e.g. 1030 or 1024:
 *   /request-detail?id=1030&step=documents&attachmentGroupedBugFixture=1
 *
 * Or seed DB rows (no URL flag needed):
 *   yarn seed:attachment-grouped-bug
 *
 * Expected with fixture ON (bug repro):
 *   - Flat list: 4 mock attachments
 *   - Grouped view: "No attachments" (bug)
 *   - Summary → Supporting Documents: count 0 / Not specified (bug)
 *
 * Compare with `?fpAttachmentFixture=1` (dot-notation sections) — grouped should work.
 */

import type { SavedAttachment } from '../services/attachmentService';
import type { Request } from '../types';

export const ATTACHMENT_GROUPED_BUG_DEMO_REQUEST_IDS = [1030, 1024] as const;
/** Site Assessment scope with split facility-power family (itssecure #8070 repro). */
export const KIRAN_8070_SCOPE_FIXTURE = [
  'opportunity',
  'facility-power',
  'large-ups',
  'power-distribution',
  'small-ups',
  'documents',
  'summary',
] as const;

/** Legacy DB `request_section` values (pre dot-notation migration). */
export const LEGACY_ATTACHMENT_SECTIONS = {
  FACILITY_POWER: 'facility_power_readiness',
  RACK: 'rack_configuration',
  SMALL_UPS: 'small_ups_requirements',
  SUPPORTING: 'supporting_documents',
} as const;

let fixtureAttachmentId = 9_807_000;

function nextFixtureAttachmentId(): number {
  fixtureAttachmentId += 1;
  return fixtureAttachmentId;
}

function mkAttachment(
  requestId: number,
  attachmentName: string,
  requestSection: string,
  captions: string,
): SavedAttachment {
  return {
    id: nextFixtureAttachmentId(),
    requestId,
    attachmentName,
    requestSection,
    captions,
    mimeType: 'application/pdf',
    fileSizeBytes: 2048,
    createdAt: '2026-06-30T12:00:00.000Z',
    updatedAt: '2026-06-30T12:00:00.000Z',
  };
}

/**
 * Four attachments using legacy section ids — flat list includes them;
 * grouped/Summary skip them when stage mapping is missing.
 */
export function createLegacyAttachmentGroupedBugFixture(
  requestId: number,
): Record<string, SavedAttachment[]> {
  return {
    [LEGACY_ATTACHMENT_SECTIONS.FACILITY_POWER]: [
      mkAttachment(
        requestId,
        'legacy-facility-power-one-line.pdf',
        LEGACY_ATTACHMENT_SECTIONS.FACILITY_POWER,
        'Mock legacy — Facility Power (8070 repro)',
      ),
    ],
    [LEGACY_ATTACHMENT_SECTIONS.RACK]: [
      mkAttachment(
        requestId,
        'legacy-rack-layout.pdf',
        LEGACY_ATTACHMENT_SECTIONS.RACK,
        'Mock legacy — Rack Configuration (8070 repro)',
      ),
    ],
    [LEGACY_ATTACHMENT_SECTIONS.SMALL_UPS]: [
      mkAttachment(
        requestId,
        'legacy-small-ups-schedule.pdf',
        LEGACY_ATTACHMENT_SECTIONS.SMALL_UPS,
        'Mock legacy — Small UPS (8070 repro)',
      ),
    ],
    [LEGACY_ATTACHMENT_SECTIONS.SUPPORTING]: [
      mkAttachment(
        requestId,
        'legacy-supporting-doc.pdf',
        LEGACY_ATTACHMENT_SECTIONS.SUPPORTING,
        'Mock legacy — Supporting Documents (8070 repro)',
      ),
    ],
  };
}

function readDevUrlFlag(param: string): boolean {
  if (typeof __DEV__ === 'undefined' || !__DEV__) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return new URLSearchParams(window.location.search).get(param) === '1';
  } catch {
    return false;
  }
}

export function isAttachmentGroupedBugFixtureEnabled(): boolean {
  return readDevUrlFlag('attachmentGroupedBugFixture');
}

/** Overlay request scope/stage so Supporting Documents uses split-scope path. */
export function getAttachmentGroupedBugRequestOverlay(): Partial<Request> | null {
  if (!isAttachmentGroupedBugFixtureEnabled()) {
    return null;
  }

  return {
    stage: 'SITE_ASSESSMENT',
    sysRequestStage: 'SITE_ASSESSMENT',
    enabledStepIds: [...KIRAN_8070_SCOPE_FIXTURE],
  };
}

export function mergeAttachmentGroupedBugFixture(
  attachmentsBySection: Record<string, SavedAttachment[]>,
  requestId: number,
): Record<string, SavedAttachment[]> {
  if (!isAttachmentGroupedBugFixtureEnabled()) {
    return attachmentsBySection;
  }

  const numericRequestId = Number.parseInt(String(requestId), 10) || 1030;
  const fixture = createLegacyAttachmentGroupedBugFixture(numericRequestId);

  // Replace with fixture-only set so repro is deterministic (not mixed with API data).
  return { ...fixture };
}

export function applyAttachmentGroupedBugRequestOverlay<T extends Request>(
  request: T,
): T {
  const overlay = getAttachmentGroupedBugRequestOverlay();
  if (!overlay) {
    return request;
  }
  return { ...request, ...overlay };
}
